import ReactDOM from "react-dom/client";
import type { Root } from "react-dom/client";
import "./index.css";
import App from "./App.tsx";
import NotFound from "./components/error/NotFound.tsx";
import { Provider } from "react-redux";
import { store } from "./reducers/store.ts";
import { RouterProvider } from "react-router-dom";
import { Theme } from "@radix-ui/themes";
import { Toaster } from "react-hot-toast";
import { createBrowserRouter } from "@datadog/browser-rum-react/react-router-v6";
import { StripTrailingSlashRedirect } from "./helpers/Navigation.tsx";
import { PostHogProvider } from "posthog-js/react";
import posthog from 'posthog-js';
import * as Sentry from "@sentry/react";
import AboutWrapper from "./components/about/AboutWrapper.tsx";
import Nav from "./components/nav/Nav.tsx";
import HomeWrapper from "./components/home/HomeWrapper.tsx";

document.documentElement.classList.add("light");

if (import.meta.env.VITE_ENV !== "local") {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    sendDefaultPii: true,
    enableLogs: true,
    environment: import.meta.env.VITE_ENV,
  });
}

posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: '2025-05-24',
  capture_exceptions: true,
  debug: import.meta.env.MODE === "development",
});

posthog.register({
  environment: import.meta.env.VITE_ENV
});

const router = createBrowserRouter([
  {
    path: "/",
    element: <StripTrailingSlashRedirect />,
    children: [
      {
        index: true,
        element: (
          <App header={<Nav />}>
            <HomeWrapper />
          </App>
        ),
      },
      {
        path: '/about',
        element: (
          <App header={<Nav />}>
            <AboutWrapper />
          </App>
        ),
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
]);

// Check if root is already created to avoid duplicate createRoot calls
const rootElement = document.getElementById("root") as HTMLElement;
const existingRoot = (rootElement as { _reactRootContainer?: Root })
  ._reactRootContainer;
if (!existingRoot) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <PostHogProvider client={posthog}>
      <Provider store={store}>
        <Theme appearance="light" scaling="100%" accentColor="gray">
          <RouterProvider router={router} />
          <Toaster
            toastOptions={{
              style: {
                maxWidth: 500,
              },
            }}
          />
        </Theme>
      </Provider>
    </PostHogProvider>
  );
} else {
  existingRoot.render(
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
      options={{
        api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
        defaults: "2025-05-24",
        capture_exceptions: true,
        debug: import.meta.env.MODE === "development",
      }}
    >
      <Provider store={store}>
        <Theme appearance="light" scaling="100%" accentColor="gray">
          <RouterProvider router={router} />
          <Toaster
            toastOptions={{
              style: {
                maxWidth: 500,
              },
            }}
          />
        </Theme>
      </Provider>
    </PostHogProvider>
  );
}
